import { useFormContext } from 'react-hook-form';
import { Body, Typography, Stack } from '@optimus-web/core';
import { LocalizedText, lt, useFeatureFlag } from '@mews/framework';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { SpacedColon, SpacedComma } from '@mews-commander/ui';
import { useResourceCategory } from '@mews-commander/legacy-entities/src/utils/useResourceCategory';
import { AvailabilityAdjustmentFormValues } from '../../../../../types';
import { BlockSetUpAsideWrapper } from '../styles';
import { FIELD_ARRAY_AVAILABILITY } from '../../../../../constants';
import { MatrixDataTestIds } from '../../constants';

export interface ReadOnlyCellProps {
    index: number;
    serviceId: string;
}

export const ReadOnlyCell: React.FC<ReadOnlyCellProps> = ({ serviceId, index }) => {
    const { watch } = useFormContext<AvailabilityAdjustmentFormValues>();
    const { resourceCategoryId, paxCount } = watch(`${FIELD_ARRAY_AVAILABILITY}.${index}`);
    const occupancyInBlocksEnabled = useFeatureFlag(FeatureFlag.occupancyInBlocksEnabled);

    const resourceCategory = useResourceCategory(resourceCategoryId, serviceId);

    if (!resourceCategory) {
        return null;
    }

    const { name, capacity, extraCapacity } = resourceCategory;

    return (
        <Stack distribution="space-between" fill itemAlignment="center">
            <BlockSetUpAsideWrapper data-test-resource-category={resourceCategoryId}>
                <Body ellipsis>{name}</Body>

                <Typography
                    whiteSpace="nowrap"
                    textStyle="bodySmall"
                    variant="secondary"
                    data-test-id={MatrixDataTestIds.AsideCellSecondaryText}
                    as="div"
                >
                    {occupancyInBlocksEnabled ? (
                        <>
                            {lt('GuestPlural')}
                            {SpacedColon}
                            <span data-test-id={MatrixDataTestIds.AsideCellGuestsValue}>{paxCount.personCount}</span>
                        </>
                    ) : (
                        <>
                            <LocalizedText textKey={TextKey.Capacity} />
                            {SpacedColon}
                            {capacity}
                            {!!extraCapacity && (
                                <>
                                    {SpacedComma}
                                    <LocalizedText textKey={TextKey.Extra} />
                                    {SpacedColon}
                                    {extraCapacity}
                                </>
                            )}
                        </>
                    )}
                </Typography>
            </BlockSetUpAsideWrapper>
        </Stack>
    );
};
